import { FormEvent } from "react"
import { useForm, usePage, router } from "@inertiajs/react"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"

export type RedirectFormData = {
  id?: number
  source_path: string
  destination: string
  redirect_type: string
}

export function RedirectForm({ initial }: { initial: RedirectFormData }) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isEdit = Boolean(initial.id)

  const form = useForm({
    source_path: initial.source_path ?? "",
    destination: initial.destination ?? "",
    redirect_type: initial.redirect_type ?? "307",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      form.put(`/admin/redirects/${initial.id}`)
    } else {
      form.post("/admin/redirects")
    }
  }

  const remove = () => {
    if (initial.id && window.confirm("Delete this redirect?")) {
      router.delete(`/admin/redirects/${initial.id}`)
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 max-w-xl space-y-6">
      <div className="space-y-2">
        <label htmlFor="source_path">Source path</label>
        <Input
          id="source_path"
          placeholder="/old-pretty-link"
          value={form.data.source_path}
          onChange={(e) => form.setData("source_path", e.target.value)}
        />
        <p className="text-xs text-ink-muted">
          The old path on this site (e.g. <code>/go/resource</code>). Trailing slashes are ignored.
        </p>
        {errors.source_path && <p className="text-xs text-danger-display">{errors.source_path}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="destination">Destination</label>
        <Input
          id="destination"
          placeholder="https://example.com/target  or  /new-path"
          value={form.data.destination}
          onChange={(e) => form.setData("destination", e.target.value)}
        />
        {errors.destination && <p className="text-xs text-danger-display">{errors.destination}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="redirect_type">Redirect type</label>
        <Select
          id="redirect_type"
          value={form.data.redirect_type}
          onChange={(e) => form.setData("redirect_type", e.target.value)}
        >
          <option value="307">307 — Temporary (recommended for affiliate links)</option>
          <option value="302">302 — Temporary</option>
          <option value="301">301 — Permanent (for content moves; cached by browsers)</option>
        </Select>
        <p className="text-xs text-ink-muted">
          Use <strong>307</strong> for affiliate/short links whose target may change. Use{" "}
          <strong>301</strong> only when the destination is permanent (e.g. an old page → its new URL).
        </p>
        {errors.redirect_type && <p className="text-xs text-danger-display">{errors.redirect_type}</p>}
      </div>

      <div className="flex items-center gap-3 border-t border-hairline pt-6">
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Saving…" : isEdit ? "Save redirect" : "Create redirect"}
        </Button>
        {isEdit && (
          <Button type="button" variant="danger" onClick={remove}>
            Delete
          </Button>
        )}
      </div>
    </form>
  )
}
